import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { UserPrefsProvider, useUserPrefs } from './contexts/UserPrefsContext';
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { Layout } from './components/Layout';

import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Today from './pages/Today';
import Habits from './pages/Habits';
import HabitDetail from './pages/HabitDetail';
import Stats from './pages/Stats';
import Templates from './pages/Templates';
import Settings from './pages/Settings';
import Landing from './pages/Landing';
import SharedHabit from './pages/SharedHabit';
import Calendar from './pages/Calendar';
import Welcome from './pages/Welcome';
import Home from './pages/Home';
import { Terms, Privacy } from './pages/Legal';
import './App.css';

const RootRedirect = () => {
  const { user, loading } = useAuth();
  const { prefs, loaded } = useUserPrefs();
  if (loading) return null;
  if (!user) return <Landing />;
  // Wait for the user's stored prefs to load before redirecting so we don't
  // briefly flash the wrong opening screen.
  if (!loaded) return null;
  return <Navigate to={prefs.home_screen === 'welcome' ? '/home' : '/today'} replace />;
};

/**
 * Listens for native deep links (e.g. `myflowhabit://today` from the iOS
 * home-screen widget) and local-notification taps and routes inside the
 * SPA. No-op on web.
 */
const DeepLinkRouter = () => {
  const navigate = useNavigate();
  useEffect(() => {
    let urlSub;
    let notifSub;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor?.isNativePlatform?.()) return;
        const { App: CapApp } = await import('@capacitor/app');
        urlSub = await CapApp.addListener('appUrlOpen', (event) => {
          try {
            const u = new URL(event.url);
            const host = u.host || '';
            if (host === 'today' || u.pathname.startsWith('/today')) navigate('/today');
            else if (host === 'home' || u.pathname.startsWith('/home')) navigate('/home');
            else navigate('/today');
          } catch {
            navigate('/today');
          }
        });
        // When the user taps a habit reminder local notification, jump to /today.
        const { onLocalNotificationTapped, queuePendingAction, snoozeHabitOneHour, drainPendingActions } = await import('./lib/localNotifications');
        notifSub = await onLocalNotificationTapped(async (action) => {
          const actionId = action?.actionId || 'tap';
          const habitId = action?.notification?.extra?.habitId;
          if (actionId === 'snooze-1h' && habitId) {
            // Schedule a follow-up notification 1h later. Stay backgrounded.
            snoozeHabitOneHour(habitId).catch(() => {});
            return;
          }
          if (actionId === 'mark-done' && habitId) {
            // Queue the write — AuthContext drains the queue once the user
            // session is restored. Then drain immediately too in case the
            // app was already foregrounded (no appResume event will fire
            // and the user expects the habit to flip to done right away).
            queuePendingAction({ actionId, habitId });
            try { await drainPendingActions(); } catch { /* no-op */ }
            navigate('/today');
            return;
          }
          // Default tap on body → open Today.
          navigate('/today');
        });
      } catch { /* no-op on web */ }
    })();
    return () => {
      try { urlSub?.remove?.(); } catch { /* no-op */ }
      try { notifSub?.(); } catch { /* no-op */ }
    };
  }, [navigate]);
  return null;
};

function App() {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch(() => {});
      });
    }
    // Native enhancements (no-op on web)
    import('./lib/native').then(({ initNativeUI }) => initNativeUI()).catch(() => {});
    // Register the iOS notification action category (Mark done + Snooze 1h)
    import('./lib/localNotifications').then(({ registerHabitNotificationActions }) => {
      registerHabitNotificationActions();
    }).catch(() => {});
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <UserPrefsProvider>
          <BrowserRouter>
            <DeepLinkRouter />
            <div className="App theme-transition grain min-h-screen">
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
                <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
                <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/share/:token" element={<SharedHabit />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />

                <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                  <Route path="/home" element={<Home />} />
                  <Route path="/today" element={<Today />} />
                  <Route path="/habits" element={<Habits />} />
                  <Route path="/habits/:id" element={<HabitDetail />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/stats" element={<Stats />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>
                <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster position="top-center" richColors />
            </div>
          </BrowserRouter>
        </UserPrefsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
